/**
 * Corpo Incasso — Required Rough-In Body
 * 
 * Fetches and displays selectable corpo incasso products.
 * Exclusive selection (radio): only one can be selected at a time.
 * On add-to-cart, the selected corpo incasso is also added as a
 * separate product in the cart.
 * 
 * @version 3.1.0
 */

(function() {
  'use strict';

  // Singleton: la barra sticky del tema (floated-form) ri-renderizza i blocchi
  // della scheda prodotto, compreso lo snippet che include QUESTO script. Senza
  // guardia lo script gira due volte, con due listener `variant:add`
  // indipendenti (ciascuno col proprio `isAdding`, quindi la guardia
  // single-flight non li vede a vicenda): scattano insieme, entrambi trovano il
  // carrello ancora senza la riga figlia e aggiungono 1 pezzo ciascuno →
  // Shopify fonde le righe → quantità 2. Succedeva a ogni aggiunta quando la
  // selezione è automatica (un solo corpo incasso disponibile).
  if (window.__bongioCorpoIncassoInit) return;
  window.__bongioCorpoIncassoInit = true;

  // ==========================================================================
  // CONFIGURATION
  // ==========================================================================

  const SELECTORS = {
    section: '#corpo-incasso-section',
    productsContainer: '#corpo-incasso-products',
    productForm: 'form[action*="/cart/add"]'
  };

  // All available items: code -> { product, card }
  const itemsMap = new Map();

  // Currently selected code (null = none)
  let selectedCode = null;

  // Visibility flags driven by Liquid (data-show-price / data-selectable on
  // the section). The block is public: everyone sees WHICH corpo incasso the
  // product needs, but price and the radio-select + add-to-cart flow are for
  // buyers only (B2B agenti/rivenditori or e-commerce countries).
  let showPrice = true;
  let selectable = true;

  // Single-flight guard: prevents the corpo incasso from being POSTed twice
  // when `variant:add` is dispatched more than once for the same submit. Two
  // dispatches do happen in practice on PDPs that mount the buy-buttons block
  // with `notification_modal:true` (the modal mounts its own <product-form>),
  // and we don't want to chase that upstream when a one-line guard here fixes
  // the user-visible duplicate-line bug deterministically.
  let isAdding = false;

  // ==========================================================================
  // INITIALIZATION
  // ==========================================================================

  function init() {
    const section = document.querySelector(SELECTORS.section);
    if (!section) return;

    const raw = section.dataset.corpi;
    if (!raw) return;

    const codes = parseCodes(raw);
    if (codes.length === 0) return;

    showPrice = section.dataset.showPrice !== 'false';
    selectable = section.dataset.selectable !== 'false';

    section.style.display = 'block';
    fetchAndRender(codes);

    // Cart integration only makes sense for buyers; for everyone else the
    // block is purely informational (no product form on the page anyway).
    if (selectable) {
      cleanupFormProperties();

      document.addEventListener('variant:add', onVariantAdd);

      const form = document.querySelector(SELECTORS.productForm);
      if (form) {
        form.addEventListener('submit', onFormSubmit, true);
      }
    }
  }

  function cleanupFormProperties() {
    const form = document.querySelector(SELECTORS.productForm);
    if (!form) return;
    form.querySelectorAll('input[name^="properties[Corpo incasso"]').forEach(function(el) { el.remove(); });
  }

  function onFormSubmit() {
    if (!selectedCode) {
      cleanupFormProperties();
    }
  }

  // ==========================================================================
  // PARSING
  // ==========================================================================

  function parseCodes(raw) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.map(c => String(c).trim()).filter(Boolean);
      }
    } catch (e) { /* not JSON */ }

    return raw.split(',').map(c => c.trim()).filter(Boolean);
  }

  // ==========================================================================
  // FETCHING
  // ==========================================================================

  async function fetchAndRender(codes) {
    const container = document.querySelector(SELECTORS.productsContainer);
    if (!container) return;

    const products = [];
    for (const code of codes) {
      const product = await fetchProduct(code);
      products.push({ code, product });
    }

    container.innerHTML = '';

    if (products.length === 0) {
      container.innerHTML = '<div class="corpo-incasso__empty">Nessun corpo incasso trovato</div>';
      return;
    }

    for (const { code, product } of products) {
      const card = createCard(code, product);
      itemsMap.set(code, { product, card });
      container.appendChild(card);
    }

    if (products.length === 1) {
      selectItem(products[0].code);
    }
  }

  /**
   * Fetch a product by code.
   * Strategy:
   *  1. Direct handle lookup: /products/{code}.js
   *  2. Predictive search → get handle → fetch full product
   *  3. Broader search with looser matching
   */
  async function fetchProduct(code) {
    console.log('[CorpoIncasso] Fetching product for code:', code);

    // Method 1: Direct handle lookup (most codes are numeric = handle matches)
    const handle = code.toLowerCase().replace(/\s+/g, '-');
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (res.ok) {
        const product = await res.json();
        console.log('[CorpoIncasso] Found via direct handle:', code, '→ price:', product.price);
        return product;
      }
    } catch (e) { /* continue */ }

    // Method 2: Predictive search
    try {
      const searchUrl = `/search/suggest.json?q=${encodeURIComponent(code)}&resources[type]=product&resources[limit]=5`;
      const res = await fetch(searchUrl);
      if (res.ok) {
        const data = await res.json();
        const results = data.resources?.results?.products || [];

        console.log('[CorpoIncasso] Search results for', code, ':', results.length, 'found');
        if (results.length > 0) {
          console.log('[CorpoIncasso] Search titles:', results.map(r => r.title));
        }

        // Try exact match by title first
        let match = results.find(p =>
          p.title.trim() === code ||
          p.title.trim().toLowerCase() === code.toLowerCase()
        );

        // If no exact match, try if any title starts with the code
        if (!match) {
          match = results.find(p =>
            p.title.trim().toLowerCase().startsWith(code.toLowerCase())
          );
        }

        // If still no match, try if any title contains the code
        if (!match) {
          match = results.find(p =>
            p.title.trim().toLowerCase().includes(code.toLowerCase())
          );
        }

        // Fallback to first result
        if (!match && results.length > 0) {
          match = results[0];
        }

        if (match) {
          console.log('[CorpoIncasso] Best match:', match.title, '→ handle:', match.handle);

          // Fetch full product data via handle
          try {
            const fullRes = await fetch(`/products/${match.handle}.js`);
            if (fullRes.ok) {
              const product = await fullRes.json();
              console.log('[CorpoIncasso] Full product loaded:', product.title, '→ price:', product.price);
              return product;
            }
          } catch (e) {
            console.warn('[CorpoIncasso] Could not fetch full product for:', match.handle);
          }

          // Return search result as fallback (normalize price)
          return normalizeSearchResult(match);
        }
      }
    } catch (e) {
      console.warn('[CorpoIncasso] Search failed for:', code, e);
    }

    console.warn('[CorpoIncasso] Product not found for code:', code);
    return null;
  }

  /**
   * Normalize a search result to have consistent price format.
   * Search API returns price as string in shop currency (e.g. "123.00")
   * while /products/{handle}.js returns price in cents (e.g. 12300).
   */
  function normalizeSearchResult(result) {
    if (!result) return result;

    // If price is a string, convert to cents
    if (typeof result.price === 'string') {
      const cleaned = result.price.replace(/[^\d.,]/g, '').replace(',', '.');
      const amount = parseFloat(cleaned);
      if (!isNaN(amount)) {
        result.price = Math.round(amount * 100);
        console.log('[CorpoIncasso] Normalized search price:', result.price, 'cents');
      }
    }

    return result;
  }

  // ==========================================================================
  // RENDERING
  // ==========================================================================

  /**
   * product.description from /products/{handle}.js is HTML — flatten it to
   * plain text for the card. DOMParser is inert: nothing gets executed or
   * fetched while parsing, unlike innerHTML on a live element.
   */
  function stripHtml(html) {
    if (!html) return '';
    try {
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return (doc.body.textContent || '').replace(/\s+/g, ' ').trim();
    } catch (e) {
      return '';
    }
  }

  function createCard(code, product) {
    // Non-buyers get a static, informational card: plain <div>, no radio
    // indicator, no click handler — just which corpo incasso is required.
    const card = document.createElement(selectable ? 'button' : 'div');
    card.className = 'corpo-incasso__card';
    card.dataset.code = code;

    if (selectable) {
      card.type = 'button';
      card.setAttribute('aria-pressed', 'false');

      // Radio indicator
      const radio = document.createElement('div');
      radio.className = 'corpo-incasso__radio';
      const dot = document.createElement('div');
      dot.className = 'corpo-incasso__radio-dot';
      radio.appendChild(dot);
      card.appendChild(radio);
    } else {
      card.classList.add('corpo-incasso__card--static');
    }

    // Image
    const imageWrap = document.createElement('div');
    imageWrap.className = 'corpo-incasso__image';

    const imgUrl = product ? getImage(product) : null;
    if (imgUrl) {
      const img = document.createElement('img');
      img.src = resizeImage(imgUrl, 120);
      img.alt = product.title || code;
      img.loading = 'lazy';
      img.width = 50;
      img.height = 50;
      imageWrap.appendChild(img);
      card.appendChild(imageWrap);
    }
    // No image = no placeholder box, cleaner look

    // Info
    const info = document.createElement('div');
    info.className = 'corpo-incasso__info';

    const name = document.createElement('div');
    name.className = 'corpo-incasso__name';
    name.textContent = product ? (product.title || code) : code;
    info.appendChild(name);

    // Sotto al titolo (che per convention Bongio È il codice articolo)
    // mostriamo la descrizione vera del corpo incasso invece del ridondante
    // "Art. <codice>". Fallback sul codice quando la descrizione manca
    // (es. fetch caduto sul risultato search senza body).
    const descText = product ? stripHtml(product.description || '') : '';
    const codeEl = document.createElement('div');
    codeEl.className = 'corpo-incasso__code';
    codeEl.textContent = descText || ('Art. ' + code);
    info.appendChild(codeEl);

    card.appendChild(info);

    // Price — buyers only (Bongio does not expose prices to end users).
    // Use != null check (not truthy) so price 0 still shows.
    const price = showPrice && product ? getPrice(product) : null;
    if (price != null) {
      const priceEl = document.createElement('div');
      priceEl.className = 'corpo-incasso__price';
      priceEl.textContent = formatPrice(price);
      card.appendChild(priceEl);
    }

    if (selectable) {
      card.addEventListener('click', () => selectItem(code));
    }
    return card;
  }

  // ==========================================================================
  // SELECTION LOGIC — exclusive (radio)
  // ==========================================================================

  function selectItem(code) {
    if (selectedCode === code) {
      deselectAll();
      selectedCode = null;
      updateFormInputs();
      dispatchUpdate();
      return;
    }

    deselectAll();

    selectedCode = code;
    const item = itemsMap.get(code);
    if (item && item.card) {
      item.card.classList.add('corpo-incasso__card--selected');
      item.card.setAttribute('aria-pressed', 'true');
    }

    updateFormInputs();
    dispatchUpdate();
  }

  function deselectAll() {
    itemsMap.forEach((item) => {
      if (item.card) {
        item.card.classList.remove('corpo-incasso__card--selected');
        item.card.setAttribute('aria-pressed', 'false');
      }
    });
  }

  function updateFormInputs() {
    const form = document.querySelector(SELECTORS.productForm);
    if (!form) return;

    form.querySelectorAll('input[name^="properties[Corpo incasso"]').forEach(el => el.remove());

    if (!selectedCode) return;

    const item = itemsMap.get(selectedCode);
    if (!item) return;

    const title = item.product ? (item.product.title || selectedCode) : selectedCode;

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'properties[Corpo incasso]';
    input.value = selectedCode + ' - ' + title;
    form.appendChild(input);
  }

  // ==========================================================================
  // ADD TO CART
  // ==========================================================================

  async function onVariantAdd(event) {
    if (!selectedCode) return;

    // Single-flight: the buy-buttons + notification-modal combo on some PDPs
    // dispatches `variant:add` twice for one submit. Without this guard the
    // user would end up with TWO corpo-incasso line items from a single click.
    if (isAdding) {
      console.log('[CorpoIncasso] Add already in flight, skipping duplicate dispatch');
      return;
    }
    isAdding = true;

    try {
      var selectedCard = document.querySelector('.corpo-incasso__card--selected');
      if (!selectedCard) {
        selectedCode = null;
        cleanupFormProperties();
        return;
      }

      const item = itemsMap.get(selectedCode);
      if (!item || !item.product) return;

      const variantId = getFirstVariantId(item.product);
      if (!variantId) {
        console.warn('[CorpoIncasso] No variant ID for:', selectedCode);
        return;
      }

      const abbinatoA = document.querySelector('.product__title')?.textContent?.trim() || '';

      // Defensive dedupe: even with the single-flight guard above, edge cases
      // (back-button, cart-section AJAX re-render, listener registered twice
      // on document because the script tag was injected twice somewhere) could
      // try to re-add the same corpo. Skip if a line for this exact (variant
      // + _abbinato_a) is already present in the cart.
      // Ambienti: l'incasso eredita l'ambiente attivo; il dedupe deve tenerne
      // conto, così lo stesso incasso in stanze diverse resta su righe separate
      // (non viene bloccato dal controllo doppioni).
      let amb = '';
      try { amb = localStorage.getItem('bongio:ambiente_attivo') || ''; } catch (_) {}
      try {
        const cartRes = await fetch('/cart.js', { credentials: 'same-origin' });
        if (cartRes.ok) {
          const cart = await cartRes.json();
          const items = Array.isArray(cart && cart.items) ? cart.items : [];
          const dup = items.some(it =>
            it.variant_id === variantId &&
            it.properties &&
            String(it.properties._abbinato_a || '') === abbinatoA &&
            String(it.properties._ambiente || '') === amb
          );
          if (dup) {
            console.log('[CorpoIncasso] Corpo già presente in cart per', abbinatoA, '— skip add');
            refreshCart();
            return;
          }
        }
      } catch (e) {
        // Non-fatal: if /cart.js failed we just proceed; the single-flight
        // guard still protects against the most common doubling scenario.
        console.warn('[CorpoIncasso] dedupe check failed, proceeding', e);
      }

      const body = JSON.stringify({
        items: [{
          id: variantId,
          quantity: 1,
          properties: Object.assign(
            { '_abbinato_a': abbinatoA },
            amb ? { '_ambiente': amb } : {}
          )
        }]
      });

      const addRes = await fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: body
      });
      const data = await addRes.json().catch(() => ({}));
      if (data.status) {
        console.warn('[CorpoIncasso] Failed to add:', data.description);
        return;
      }
      console.log('[CorpoIncasso] Added to cart:', selectedCode);
      refreshCart();
    } catch (err) {
      console.error('[CorpoIncasso] Add to cart error:', err);
    } finally {
      isAdding = false;
    }
  }

  function refreshCart() {
    const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    if (!cart) return;

    const sectionsToRender = cart.getSectionsToRender
      ? cart.getSectionsToRender().map(s => s.id)
      : [];

    if (sectionsToRender.length > 0) {
      fetch(`${window.location.pathname}?sections=${sectionsToRender.join(',')}`)
        .then(res => res.json())
        .then(sections => {
          if (cart.getSectionsToRender) {
            cart.getSectionsToRender().forEach(section => {
              const html = sections[section.id];
              if (html) {
                const el = document.getElementById(section.id);
                if (el) {
                  el.innerHTML = new DOMParser()
                    .parseFromString(html, 'text/html')
                    .getElementById(section.id)?.innerHTML || html;
                }
              }
            });
          }
        })
        .catch(err => console.warn('[CorpoIncasso] Cart refresh error:', err));
    }

    fetch('/cart.js')
      .then(res => res.json())
      .then(cartData => {
        const bubbles = document.querySelectorAll('.cart-count-bubble span, [data-cart-count]');
        bubbles.forEach(el => { el.textContent = cartData.item_count; });
      })
      .catch(() => {});
  }

  // ==========================================================================
  // EVENT DISPATCHING
  // ==========================================================================

  function dispatchUpdate() {
    const selected = selectedCode ? getSelectedData() : null;
    document.dispatchEvent(new CustomEvent('corpo-incasso:update', {
      bubbles: true,
      detail: { selected }
    }));
  }

  function getSelectedData() {
    if (!selectedCode) return null;
    const item = itemsMap.get(selectedCode);
    if (!item) return null;
    return {
      code: selectedCode,
      title: item.product ? (item.product.title || selectedCode) : selectedCode,
      price: item.product ? getPrice(item.product) : null,
      variantId: item.product ? getFirstVariantId(item.product) : null,
      image: item.product ? getImage(item.product) : null
    };
  }

  // ==========================================================================
  // HELPERS
  // ==========================================================================

  function getImage(product) {
    if (product.featured_image) {
      return typeof product.featured_image === 'string'
        ? product.featured_image
        : product.featured_image.src || product.featured_image;
    }
    if (product.image) return product.image;
    if (product.images && product.images.length > 0) {
      const first = product.images[0];
      return typeof first === 'string' ? first : first.src || first;
    }
    return null;
  }

  function resizeImage(url, width) {
    if (!url) return url;
    return url.replace(/\.(jpg|jpeg|png|gif|webp)(\?.*)?$/i, `_${width}x.$1$2`);
  }

  function getPrice(product) {
    // /products/{handle}.js: price in cents (number)
    if (typeof product.price === 'number') return product.price;
    // Search result fallback: price could be string
    if (typeof product.price === 'string') {
      const cleaned = product.price.replace(/[^\d.,]/g, '').replace(',', '.');
      const amount = parseFloat(cleaned);
      if (!isNaN(amount)) return Math.round(amount * 100);
    }
    if (typeof product.price_min === 'number') return product.price_min;
    return null;
  }

  function getFirstVariantId(product) {
    if (product.variants && product.variants.length > 0) {
      return product.variants[0].id;
    }
    return null;
  }

  function formatPrice(price) {
    if (window.Shopify && window.Shopify.formatMoney) {
      return window.Shopify.formatMoney(price);
    }
    const amount = price / 100;
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  }

  // ==========================================================================
  // BOOTSTRAP
  // ==========================================================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
